// @flow

import type { SoilJobOperation } from 'hfty-operator-soil-jobs/types'

// Libs
import React, { Component } from 'react'
import _find from 'lodash/find'
import _isEmpty from 'lodash/isEmpty'

// Utils
import traverse from 'fc-utils/traverse'

// Components
import Button from '@react-mdc/button'
import { Errors } from 'hfty-components-errors'
import Select from 'react-select'
import { FormattedMessage } from 'react-intl'

// Messages
import messages from '../messages'

// Constants
import { PROP_ASSIGNED_TO, PROP_STATUS, PROP_SUBFIELD } from 'hfty-model-soil-jobs'
import { SHIPPING, RESULTS_POSTED, ORDER_TEST } from 'fc-model-sampling-jobs'

// Components
import FormField from '@react-mdc/form-field'

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 300,
    backgroundColor: '#fff',
    color: '#fff',
    boxShadow: '-2px 0 2px rgba(0, 0, 0, 0.2)',
    padding: 15,
    overflow: 'auto',
  },
  componentStyle: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    minWidth: 350,
    maxWidth: '100vw',
    backgroundColor: '#fff',
    boxShadow: '-2px 0 2px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
  passwordError: {
    color: 'red',
  },
  dropDown: {
    float: 'right',
  },
  formField: {
    width: 300,
  },
  buttonDiv: {
    marginTop: 20,
  },
}

type Props = {
  operation: SoilJobOperation,
  fieldSelectorOptions: Array,
  userSelectorOptions: Array,
  jobStatusOptions: Array,
  subfields: Array,
  onChange: Function,
  onCancel: Function,
  onSubmit: Function,
}

// Getters
const getStatus = traverse(['operation', 'model', PROP_STATUS], { default: '' })
const getAssignedTo = traverse(['operation', 'model', PROP_ASSIGNED_TO], { default: '' })
const getSubfield = function (props: Object): number {
  let sf = _find(props.subfields, { id: props.operation.model[PROP_SUBFIELD] })
  return _find(props.fieldSelectorOptions, { value: sf.parent })
}

export default class EditSoilJob extends Component {
  props: Props
  onSubmit: Function
  onCancel: Function

  constructor(props: Props) {
    super(props)

    this.onChangeSubfield = this.onChangeSubfield.bind(this)
    this.onChangeAssignedTo = this.onChangeAssignedTo.bind(this)
    this.onChangeStatus = this.onChangeStatus.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  onSubmit(e: Event) {
    e.preventDefault()

    this.props.onSubmit([this.props.operation])
  }

  onCancel(e: Event) {
    e.preventDefault()

    this.props.onCancel([this.props.operation])
  }

  onChangeSubfield(e: Event & { currentTarget: HTMLInputElement }) {
    let subfield = _find(this.props.subfields, { parent: e.value })
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_SUBFIELD]: subfield.id,
      },
    })
  }

  onChangeAssignedTo(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_ASSIGNED_TO]: e.value,
      },
    })
  }

  onChangeStatus(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [PROP_STATUS]: e.value,
      },
    })
  }

  render(): React.Element<any> {
    if (_isEmpty(this.props.operation) || this.props.operation.operation !== 'EDIT') {
      return null
    }

    const disableEdit =
      getStatus(this.props) === SHIPPING ||
      getStatus(this.props) === RESULTS_POSTED ||
      getStatus(this.props) === ORDER_TEST

    return (
      <form style={styles.container} onSubmit={this.onSubmit}>
        <FormField>
          {disableEdit && (
            <div>
              <FormattedMessage {...messages['cannotEditJob']} /> {getStatus(this.props)}.
            </div>
          )}
          <Errors errors={this.props.operation.errors} messages={messages} />
        </FormField>
        <FormField key="subfield-selector" style={styles.formField}>
          <label htmlFor="subfield-selector">Field</label>
          <Select
            name="subfield-selector"
            value={getSubfield(this.props)}
            options={this.props.fieldSelectorOptions}
            onChange={this.onChangeSubfield}
            style={styles.dropDown}
            disabled={disableEdit}
          />
        </FormField>
        <FormField key="assignedTo-selector" style={styles.formField}>
          <label htmlFor="assignedTo-selector">Assigned To</label>
          <Select
            name="assignedTo-selector"
            options={this.props.userSelectorOptions}
            value={getAssignedTo(this.props)}
            onChange={this.onChangeAssignedTo}
            style={styles.dropDown}
            disabled={disableEdit}
          />
        </FormField>
        <FormField key="status-selector" style={styles.formField}>
          <label htmlFor="status-selector">Status</label>
          <Select
            name="status-selector"
            options={this.props.jobStatusOptions}
            style={styles.dropDown}
            // value={getStatus(this.props)} ** If we enable this, put this line back
            placeholder={getStatus(this.props)}
            onChange={this.onChangeStatus}
            disabled
          />
        </FormField>
        <div style={styles.buttonDiv}>
          <Button raised primary disabled={disableEdit} onClick={this.onSubmit}>
            {'Submit'}
          </Button>
          <Button raised onClick={this.onCancel} primary style={{ marginLeft: 10 }}>
            {'Cancel'}
          </Button>
        </div>
      </form>
    )
  }
}
